import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { Helmet } from "react-helmet";
import {
  Button,
  VStack,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  List,
  ListItem,
  ListIcon,
  useDisclosure,
  Icon,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { gql } from "../../../../__generated__";
import Loading from "_components/Loading";
import RequestFailed from "_components/RequestFailed";
import ApplicationSuccess from "_components/job-applications/application-success";
import Navbar from "_components/Navbar/Navbar";
import Footer from "_components/Footer";
import JobApplicationForm from "./job-application-form";
import { logEvent } from "helpers/analytics";
import { FaArrowLeft } from "react-icons/fa";
import { useAuth0 } from "@auth0/auth0-react";
import { useEmbed } from "../../../../hooks/useEmbed";
import { useEmbedResize } from "../../../../hooks/useEmbedResize";

interface CompleteProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
}

function CompleteProfileModal({
  isOpen,
  onClose,
  email,
}: CompleteProfileModalProps) {
  const { id } = useParams<{ id: string }>();

  const handleCompleteProfile = () => {
    const url = `${
      process.env.REACT_APP_PRO_APPLICATION_URL
    }/apply?src_email=${encodeURIComponent(email)}`;

    window.open(url, "_blank");
    logEvent("job_application/complete_laborpass_clicked", {
      job_id: id,
      email,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent mx={4} rounded={0}>
        <ModalHeader
          fontSize={{
            base: "14px",
            lg: "20px",
          }}
          fontWeight="medium"
          color="#0E212D"
        >
          Track your job application!
        </ModalHeader>
        <ModalCloseButton mt={1} />
        <ModalBody pb={6}>
          <Text mb={4} color="#6E6E6E" lineHeight="1.6">
            {`You've successfully applied for this job — great move!`}
          </Text>
          <Text mb={4} color="#6E6E6E" lineHeight="1.6">
            {`But there's one more step to stand out. Completing your profile and
            creating your LaborPass helps employers take you seriously. It shows
            your skills, experience, and work history in one verified place.`}
          </Text>

          <Text fontWeight="medium" mb={3} color="#0E212D">
            Why it matters:
          </Text>
          <List spacing={2} mb={6}>
            <ListItem display="flex" alignItems="flex-start">
              <ListIcon as={CheckIcon} color="prussianBlue.500" mt={1} />
              <Text color="#6E6E6E">
                You will be able to track the progress of your job applications.
              </Text>
            </ListItem>
            <ListItem display="flex" alignItems="flex-start">
              <ListIcon as={CheckIcon} color="prussianBlue.500" mt={1} />
              <Text color="#6E6E6E">
                Employers are more likely to contact pros with complete
                profiles.
              </Text>
            </ListItem>
            <ListItem display="flex" alignItems="flex-start">
              <ListIcon as={CheckIcon} color="prussianBlue.500" mt={1} />
              <Text color="#6E6E6E">LaborPass acts like your digital CV.</Text>
            </ListItem>
            <ListItem display="flex" alignItems="flex-start">
              <ListIcon as={CheckIcon} color="prussianBlue.500" mt={1} />
              <Text color="#6E6E6E">
                Verified profiles are ranked higher in job matches.
              </Text>
            </ListItem>
          </List>

          <Button
            colorScheme="flatteredFlamingo"
            size="lg"
            width="100%"
            onClick={handleCompleteProfile}
          >
            👉 Complete my LaborPass
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

const GET_JOB_AD = gql(`
  query GetJobAdForApplication($id: String!) {
    getEmployerJobAd(id: $id) {
      id
      title
      description
      employmentType
      companyName
      organization {
        name
      }
      principal {
        name
      }
      location {
        area
        state
        country
      }
      salaryDetails {
        starting
        cap
        currencyCode
      }
      duration {
        value
        type
      }
      tags {
        id
        name
      }
      categories {
        name
      }
      hasApplied
      canApply
    }
  }
`);

const CREATE_RECRUITER_JOB_APPLICATION_LOGGED_IN = gql(`
  mutation CreateRecruiterJobApplicationLoggedIn($input: CreateJobApplicationDto!) {
    createRecruiterJobApplicationLoggedIn(input: $input) {
      id
      applicant {
        pro {
          id
        }
      }
      applicantSnapshot {
        email
        firstName
        lastName
        phoneNumber
        yearsOfExperience
      }
      status
      createdAt
    }
  }
`);

const CREATE_RECRUITER_JOB_APPLICATION_NOT_LOGGED_IN = gql(`
  mutation CreateRecruiterJobApplicationNotLoggedIn($input: CreateJobApplicationDto!) {
    createRecruiterJobApplicationNotLoggedIn(input: $input) {
      id
      applicant {
        pro {
          id
        }
      }
      applicantSnapshot {
        email
        firstName
        lastName
        phoneNumber
        yearsOfExperience
      }
      status
      createdAt
    }
  }
`);

export default function JobApplicationPage() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isAuthenticated } = useAuth0();
  const { isEmbedded } = useEmbed();
  useEmbedResize();

  const handleModalClose = () => {
    onClose();
    history.push(`/jobs/${id}${isEmbedded ? "?embed=true" : ""}`);
  };

  // State for storing user data when showing the modal
  const [guestApplicantData, setGuestApplicantData] =
    useState<{ email: string } | null>(null);

  const { data, loading, error } = useQuery(GET_JOB_AD, {
    variables: { id: id! },
    skip: !id,
  });

  const [
    createJobApplicationLoggedIn,
    { loading: isCreatingJobApplicationLoggedIn },
  ] = useMutation(CREATE_RECRUITER_JOB_APPLICATION_LOGGED_IN);

  const [
    createJobApplicationNotLoggedIn,
    { loading: isCreatingJobApplicationNotLoggedIn },
  ] = useMutation(CREATE_RECRUITER_JOB_APPLICATION_NOT_LOGGED_IN);

  const handleApplicationResult = (
    application: {
      id: string;
      applicant?: { pro?: { id: string } | null } | null;
    },
    normalizedEmail: string
  ) => {
    logEvent("job_application_submitted", {
      job_id: id,
      application_id: application.id,
    });

    const hasProFields = application.applicant?.pro?.id;

    if (!hasProFields) {
      setGuestApplicantData({ email: normalizedEmail });
      onOpen();
    } else {
      toast({
        title: "Application submitted",
        description: "Your job application has been submitted successfully!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      history.push(`/jobs/${id}${isEmbedded ? "?embed=true" : ""}`);
    }
  };

  const handleSubmit = async (formData: any) => {
    const { email, ...rest } = formData;
    const normalizedEmail = email.toLowerCase();

    const input = {
      ad: id!,
      ...rest,
      email: normalizedEmail,
    };

    try {
      if (isAuthenticated) {
        const result = await createJobApplicationLoggedIn({
          variables: { input },
        });
        if (result.data?.createRecruiterJobApplicationLoggedIn) {
          handleApplicationResult(
            result.data.createRecruiterJobApplicationLoggedIn,
            normalizedEmail
          );
        }
      } else {
        const result = await createJobApplicationNotLoggedIn({
          variables: { input },
        });
        if (result.data?.createRecruiterJobApplicationNotLoggedIn) {
          handleApplicationResult(
            result.data.createRecruiterJobApplicationNotLoggedIn,
            normalizedEmail
          );
        }
      }
    } catch (error: any) {
      toast({
        title: "Application failed",
        description:
          error.message ||
          "Failed to submit your application. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCancel = () => {
    history.push(`/jobs/${id}${isEmbedded ? "?embed=true" : ""}`);
  };

  useEffect(() => {
    if (data?.getEmployerJobAd) {
      logEvent("job_application_page_viewed", {
        job_id: id,
        job_title: data.getEmployerJobAd.title,
      });
    }
  }, [data, id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loading />
      </div>
    );
  }

  if (error || !data?.getEmployerJobAd) {
    return <RequestFailed text={error?.message || "Job not found"} />;
  }

  const jobAd = data.getEmployerJobAd;

  if (jobAd.hasApplied && !jobAd.canApply) {
    return (
      <>
        <Helmet>
          <title>Application Submitted - {jobAd.title} - LaborHack Jobs</title>
        </Helmet>
        <Navbar colorScheme="prussianBlue" />
        <ApplicationSuccess
          jobTitle={jobAd.title}
          companyName={jobAd.companyName}
          jobId={id}
          message="You've already applied for this job!"
          showDashboardLink={true}
        />
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Apply for {jobAd.title} - LaborHack Jobs</title>
      </Helmet>
      <Navbar colorScheme="prussianBlue" />
      <VStack spacing={0} maxW={"99vw"} minH={"100vh"}>
        <section className="px-3 lg:px-20 py-8 lg:py-16 w-full max-w-4xl">
          <div className="mb-8">
            <Button
              variant="link"
              colorScheme="prussianBlue"
              fontSize={{
                base: "14px",
                lg: "16px",
              }}
              onClick={handleCancel}
              className="mb-4 px-0"
              leftIcon={<Icon as={FaArrowLeft} w={4} h={4} />}
            >
              Back to job
            </Button>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#0E212D] mb-2">
              Apply for {jobAd.title}
            </h1>
            <p className="text-[#6E6E6E] text-lg">
              {jobAd.companyName} • {jobAd.location.area},{" "}
              {jobAd.location.state}
            </p>
          </div>

          <JobApplicationForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={
              isCreatingJobApplicationLoggedIn ||
              isCreatingJobApplicationNotLoggedIn
            }
            jobId={id}
          />
        </section>
      </VStack>
      <Footer />
      <CompleteProfileModal
        isOpen={isOpen}
        onClose={handleModalClose}
        email={guestApplicantData?.email || ""}
      />
    </>
  );
}
